import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Eye,
  FileText,
  FilePlus,
  PenLine,
  User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HiringProcessAssigneeRole,
  HiringProcessItemStatus,
  HiringProcessItemType,
} from "@api/shared-runtime/enums";
import { useTRPC, type RouterOutputs } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { FormResponseDialog } from "../form-response-dialog";
import { PdfSignDialog } from "../pdf/pdf-sign-dialog";
import { SigningDocumentSetupDialog } from "../pdf/signing-document-setup-dialog";
import { statusIcon, statusLabel } from "./helpers";
import { CandidateContentPanel } from "./candidate-content-panel";
import { ProviderUploadsPanel } from "./provider-uploads-panel";

type HiringProcessInstanceItem =
  RouterOutputs["hiringProcess"]["listCandidates"][number]["items"][number];

interface HiringProcessInstanceItemRowProps {
  item: HiringProcessInstanceItem;
  index: number;
  canEditOrgItems: boolean;
  onStatusChange: (id: string, status: HiringProcessItemStatus) => void;
  isUpdating: boolean;
  /** Set when the row is rendered for a specific candidate (enables upload UI). */
  candidateUserId?: string;
  jobPostId?: string;
  organizationBusinessUnitId?: string;
}

export function HiringProcessInstanceItemRow({
  item,
  index,
  canEditOrgItems,
  onStatusChange,
  isUpdating,
  candidateUserId,
  jobPostId,
  organizationBusinessUnitId,
}: HiringProcessInstanceItemRowProps) {
  const isOrgItem =
    item.assigneeRole === HiringProcessAssigneeRole.Organization;
  const canEdit = canEditOrgItems && isOrgItem;
  const isFormItem = item.itemType === HiringProcessItemType.Form;
  const isSigningItem = item.itemType === HiringProcessItemType.DocumentSigning;
  const isDocumentUpload =
    item.itemType === HiringProcessItemType.DocumentUpload;
  const isCompleted = item.status === HiringProcessItemStatus.Completed;

  const [formResponseOpen, setFormResponseOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const trpc = useTRPC();

  // The form whose signing we track: a Form item's own form, or a
  // document_signing item's per-candidate form (set on the progress row).
  const signingFormId = isSigningItem ? item.fileFormId : item.formId;
  // A document_signing slot the org hasn't set up yet (no per-candidate form).
  const needsSetup = isSigningItem && !item.fileFormId && canEditOrgItems;

  // For file-form items, learn whether the ORG must sign and whether it's the
  // org's turn now (multi-party signing). No-op for native forms / non-forms.
  const canQuerySigning =
    !!signingFormId &&
    !!candidateUserId &&
    !!jobPostId &&
    !!organizationBusinessUnitId &&
    canEditOrgItems;
  const { data: signingState } = useQuery(
    trpc.organizationForm.getFileFormSigningState.queryOptions(
      {
        organizationBusinessUnitId: organizationBusinessUnitId ?? "",
        formId: signingFormId ?? "",
        candidateUserId: candidateUserId ?? "",
        jobPostId: jobPostId ?? "",
        hiringProcessItemId: item.hiringProcessItemId,
      },
      { enabled: canQuerySigning },
    ),
  );
  const showOrgSigning = !!signingState?.requiresOrgSignature;

  // Org-side document-upload panel only applies to org-assigned items —
  // for provider-assigned uploads we render a read-only `ProviderUploadsPanel`
  // below so the org can see what the provider submitted instead of being
  // asked to upload it themselves.
  const renderCandidateContentPanel =
    isDocumentUpload &&
    isOrgItem &&
    canEditOrgItems &&
    candidateUserId &&
    jobPostId &&
    organizationBusinessUnitId;
  const renderProviderUploadsPanel =
    isDocumentUpload && !isOrgItem && canEditOrgItems;

  return (
    <div>
      <div className="grid min-w-0 grid-cols-[1.25rem_1rem_minmax(0,1fr)] items-start gap-x-2 gap-y-3 bg-white p-3 sm:p-4">
        <span className="text-xs text-slate-400 font-mono mt-0.5 w-5 text-right shrink-0">
          {index + 1}.
        </span>
        {statusIcon(item.status as HiringProcessItemStatus)}
        <div className="min-w-0 [overflow-wrap:anywhere]">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={`text-sm ${
                isCompleted ? "text-slate-400 line-through" : "text-slate-800"
              }`}
            >
              {item.title}
            </span>
            {!item.isRequired && (
              <span className="text-xs text-slate-400">(optional)</span>
            )}
            {isFormItem && (
              <Badge
                variant="outline"
                className="text-xs h-4 px-1.5 bg-purple-50 text-purple-700 border-purple-200"
              >
                <FileText size={9} className="mr-1" />
                Form
              </Badge>
            )}
            {item.itemType === HiringProcessItemType.DocumentShare && (
              <Badge
                variant="outline"
                className="text-xs h-4 px-1.5 bg-indigo-50 text-indigo-700 border-indigo-200"
              >
                Shared
              </Badge>
            )}
            {isDocumentUpload && (
              <Badge
                variant="outline"
                className="text-xs h-4 px-1.5 bg-amber-50 text-amber-700 border-amber-200"
              >
                Upload
              </Badge>
            )}
            {isSigningItem && (
              <Badge
                variant="outline"
                className="text-xs h-4 px-1.5 bg-green-50 text-green-700 border-green-200"
              >
                PDF
              </Badge>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
          )}
          {item.notes && (
            <p className="text-xs text-slate-400 italic mt-0.5">
              Note: {item.notes}
            </p>
          )}
        </div>

        <div className="col-span-3 flex min-w-0 flex-wrap items-center gap-3">
          <Badge
            variant="outline"
            className={`text-xs h-5 px-1.5 ${
              isOrgItem
                ? "bg-green-50 text-green-700 border-green-200"
                : "bg-purple-50 text-purple-700 border-purple-200"
            }`}
          >
            {isOrgItem ? (
              <Building2 size={10} className="mr-1" />
            ) : (
              <User size={10} className="mr-1" />
            )}
            {isOrgItem ? "Org" : "Provider"}
          </Badge>

          {needsSetup && (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setSetupOpen(true)}
            >
              <FilePlus size={12} className="mr-1" />
              Set up PDF
            </Button>
          )}

          {showOrgSigning &&
            (signingState?.orgStatus === "completed" ? (
              <span className="text-xs text-green-600">Org signed</span>
            ) : signingState?.canOrgSignNow ? (
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setSignOpen(true)}
              >
                <PenLine size={12} className="mr-1" />
                Sign
              </Button>
            ) : (
              <span className="text-xs text-slate-400">Awaiting candidate</span>
            ))}

          {(isFormItem || isSigningItem) &&
          isCompleted &&
          item.formSubmissionId ? (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setFormResponseOpen(true)}
            >
              <Eye size={12} className="mr-1" />
              {isSigningItem ? "View PDF" : "View Response"}
            </Button>
          ) : canEdit && !isDocumentUpload ? (
            // DocumentUpload items hide the status dropdown — the panel
            // below has a "Mark complete" button that already handles
            // completion (and requires at least one file or link first),
            // so the dropdown would be duplicative.
            <Select
              value={item.status}
              onValueChange={(value) =>
                onStatusChange(item.id, value as HiringProcessItemStatus)
              }
              disabled={isUpdating}
            >
              <SelectTrigger
                aria-label={`Status for ${item.title}`}
                className="w-full sm:w-44"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(HiringProcessItemStatus).map((status) => (
                  <SelectItem key={status} value={status} className="text-xs">
                    {statusLabel(status as HiringProcessItemStatus)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-xs text-slate-500">
              {statusLabel(item.status as HiringProcessItemStatus)}
            </span>
          )}
        </div>
      </div>

      {(isFormItem || isSigningItem) && item.formSubmissionId && (
        <FormResponseDialog
          open={formResponseOpen}
          onOpenChange={setFormResponseOpen}
          formSubmissionId={item.formSubmissionId}
        />
      )}

      {renderCandidateContentPanel &&
        candidateUserId &&
        jobPostId &&
        organizationBusinessUnitId && (
          <CandidateContentPanel
            item={item}
            candidateUserId={candidateUserId}
            jobPostId={jobPostId}
            organizationBusinessUnitId={organizationBusinessUnitId}
            onStatusChange={onStatusChange}
            isUpdating={isUpdating}
          />
        )}

      {renderProviderUploadsPanel && <ProviderUploadsPanel item={item} />}

      {showOrgSigning && candidateUserId && jobPostId && signingFormId && (
        <PdfSignDialog
          open={signOpen}
          onOpenChange={setSignOpen}
          formId={signingFormId}
          anchor={{
            kind: "hiring",
            jobPostId,
            hiringProcessItemId: item.hiringProcessItemId,
            candidateUserId,
          }}
          title={item.title}
          onSigned={() => void queryClient.invalidateQueries()}
        />
      )}

      {isSigningItem &&
        canEditOrgItems &&
        candidateUserId &&
        jobPostId &&
        organizationBusinessUnitId && (
          <SigningDocumentSetupDialog
            open={setupOpen}
            onOpenChange={setSetupOpen}
            organizationBusinessUnitId={organizationBusinessUnitId}
            jobPostId={jobPostId}
            candidateUserId={candidateUserId}
            hiringProcessItemId={item.hiringProcessItemId}
            onDone={() => void queryClient.invalidateQueries()}
          />
        )}
    </div>
  );
}
